import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, ShieldCheck, Sparkles, Award } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { IMAGES, STATS } from "../mock";

const VALUES = [
  { icon: ShieldCheck, title: "Medical Excellence", text: "Physician-led care backed by rigorous safety standards and the latest evidence-based techniques." },
  { icon: Sparkles, title: "Natural Results", text: "An artful, restrained approach that enhances your features while preserving what makes you, you." },
  { icon: HeartHandshake, title: "Personalised Care", text: "Every treatment plan is designed around your goals, anatomy and lifestyle — never one-size-fits-all." },
  { icon: Award, title: "Certified Expertise", text: "A certified cosmetic clinic trusted by thousands across the Edmonton area for over a decade." },
];

export default function About() {
  useReveal();
  return (
    <main className="pt-20">
      {/* Page hero */}
      <section className="relative py-32 bg-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src={IMAGES.interior2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/40" />
        </div>
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 text-center">
          <span className="eyebrow text-gold">About Aesthetix MD</span>
          <h1 className="font-display text-5xl lg:text-6xl font-light text-cream mt-5 leading-tight">
            Redefining beauty, <span className="italic text-gold">the medical way</span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="bg-cream py-28">
        <div className="max-w-[1300px] mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative reveal">
            <div className="img-zoom rounded-sm overflow-hidden">
              <img src={IMAGES.aboutPortrait} alt="Our clinic" className="w-full h-[560px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-28 h-28 border border-gold hidden md:block" />
          </div>
          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-gold">Our Story</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-ink leading-tight">
              The number one experts in cosmetic medical treatments
            </h2>
            <p className="font-sans text-muted-ink text-[15px] leading-[1.9] mt-7">
              Welcome to Aesthetix MD — a high-end medical spa nestled in Edmonton’s vibrant Brewery District. When it comes to your appearance, you deserve the best care that money can buy, delivered by a team who treat your confidence as their craft.
            </p>
            <p className="font-sans text-muted-ink text-[15px] leading-[1.9] mt-4">
              We are a certified cosmetic clinic providing professional, safe and personalised services. From injectables and regenerative therapies to bespoke medical facials, we blend clinical precision with a genuine warmth that turns every visit into a ritual.
            </p>
            <Link to="/contact" className="inline-block btn-gold mt-9">Book a Complimentary Consultation</Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-charcoal py-28 grain relative">
        <div className="max-w-[1300px] mx-auto px-6 relative z-10">
          <div className="text-center max-w-2xl mx-auto reveal">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-gold">Our Philosophy</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-cream leading-tight">
              Guided by care, defined by <span className="italic text-gold">detail</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {VALUES.map((v, i) => (
              <div key={v.title} className="lux-card bg-charcoal-soft border border-white/5 rounded-sm p-8 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <v.icon size={30} strokeWidth={1.2} className="text-gold" />
                <h3 className="font-display text-2xl text-cream mt-6">{v.title}</h3>
                <p className="font-sans text-cream/55 text-sm leading-relaxed mt-3">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-cream-deep py-20">
        <div className="max-w-[1300px] mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center reveal">
              <div className="font-display text-5xl lg:text-6xl text-gold font-light">{s.value}</div>
              <div className="eyebrow text-muted-ink mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
